import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import EmployeeCard from "../components/EmployeeCard";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Search, SlidersHorizontal, MapPin, UserPlus, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const [loading, setLoading] = useState(true);
  const [guestDismissed, setGuestDismissed] = useState(
    typeof window !== "undefined" && localStorage.getItem("ksb_guest_browse_dismissed") === "1"
  );
  const activeCat = params.get("category") || "";
  const activeCity = params.get("city") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (activeCat) p.category = activeCat;
    if (search) p.search = search;
    if (activeCity) p.city = activeCity;
    api.get("/employees", { params: p }).then((r) => {
      setEmployees(r.data);
      setLoading(false);
    });
  }, [activeCat, search, activeCity]);

  const setCat = (slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set("category", slug);
    else next.delete("category");
    setParams(next);
  };

  const setCity = (city) => {
    const next = new URLSearchParams(params);
    if (city && city !== "__all__") next.set("city", city);
    else next.delete("city");
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FE]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="label-eyebrow mb-3">Browse</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
          {activeCat ? categories.find((c) => c.slug === activeCat)?.name || "Staff" : "All Staff"}
        </h1>
        <p className="text-[#5B6478] mt-2">{employees.length} professionals available · contact info hidden</p>

        {/* Guest-mode prompt: nudge visitors to create an employer account OR
            keep browsing. The dismiss state is sticky (localStorage) and the
            banner also stays hidden once the visitor has signed in. */}
        {!user && !guestDismissed && (
          <div
            data-testid="guest-employer-prompt"
            className="mt-6 rounded-xl border border-[#7C3AED]/30 bg-gradient-to-r from-[#EEF0FA] via-white to-[#F3EBFF] p-5 flex items-start gap-4 relative overflow-hidden"
          >
            <div className="w-11 h-11 rounded-lg bg-[#7C3AED] text-white flex items-center justify-center shrink-0 shadow-md">
              <UserPlus className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-[#0F1E4B]">Hiring staff? Create a free employer account.</div>
              <p className="text-sm text-[#5B6478] mt-1 leading-relaxed">
                You can keep browsing as a guest — but you'll need to sign up when you find someone you want to match with.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  asChild
                  data-testid="guest-signup-btn"
                  className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-white h-10"
                >
                  <Link to="/signup?role=customer">Create employer account</Link>
                </Button>
                <Button
                  data-testid="guest-dismiss-btn"
                  variant="outline"
                  className="border-[#E4E7F5] h-10"
                  onClick={() => {
                    localStorage.setItem("ksb_guest_browse_dismissed", "1");
                    setGuestDismissed(true);
                  }}
                >
                  Continue as guest
                </Button>
              </div>
            </div>
            <button
              aria-label="Dismiss"
              onClick={() => {
                localStorage.setItem("ksb_guest_browse_dismissed", "1");
                setGuestDismissed(true);
              }}
              className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-white/70 flex items-center justify-center text-[#5B6478]"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="mt-8 sticky top-16 bg-[#F8F9FE]/95 backdrop-blur-xl z-30 py-4 -mx-6 px-6 lg:-mx-10 lg:px-10 border-b border-[#E4E7F5]">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-[#5B6478] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="browse-search"
                placeholder="Search by name, skill, or keyword..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 border-[#E4E7F5] focus:ring-[#1E3A8A]"
              />
            </div>
            <Select value={activeCity || "__all__"} onValueChange={setCity}>
              <SelectTrigger data-testid="browse-city" className="w-[190px] h-11 border-[#E4E7F5]">
                <MapPin className="w-4 h-4 mr-2 text-[#5B6478]" />
                <SelectValue placeholder="Any city" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Any city</SelectItem>
                {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              data-testid="browse-clear"
              variant="outline"
              className="border-[#E4E7F5]"
              onClick={() => {
                setSearch("");
                setCat("");
                setCity("");
              }}
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" /> Reset
            </Button>
          </div>

          <div className="mt-4 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            <button
              data-testid="cat-filter-all"
              onClick={() => setCat("")}
              className={`shrink-0 px-4 py-2 rounded-md text-sm border ${
                !activeCat
                  ? "bg-[#1E3A8A] text-[#F8F9FE] border-[#1E3A8A]"
                  : "border-[#E4E7F5] hover:bg-[#EEF0FA]"
              }`}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                data-testid={`cat-filter-${c.slug}`}
                onClick={() => setCat(c.slug)}
                className={`shrink-0 px-4 py-2 rounded-md text-sm border whitespace-nowrap ${
                  activeCat === c.slug
                    ? "bg-[#1E3A8A] text-[#F8F9FE] border-[#1E3A8A]"
                    : "border-[#E4E7F5] hover:bg-[#EEF0FA]"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {loading
            ? Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-80 bg-[#EEF0FA] animate-pulse rounded-md" />
              ))
            : employees.map((e) => <EmployeeCard key={e.id} emp={e} />)}
        </div>
        {!loading && employees.length === 0 && (
          <div className="text-center py-20 text-[#5B6478]">No staff found. Try clearing filters.</div>
        )}
      </div>
      <Footer />
    </div>
  );
}
